import { ScanResult } from './martech-scanner-constants';
import { FireSource } from './tag-fire-constants';
import { CHANNELS, Channel, ChannelHealthContext, CheckResult } from './channel-health-constants';

/** Ported from app.py's chCtx(). */
export const buildChannelHealthContext = (result: ScanResult): ChannelHealthContext => {
  const cats: Record<string, string[]> = {};
  const names: string[] = [];
  Object.entries(result.detections).forEach(([cat, m]) => {
    cats[cat] = Object.keys(m);
    names.push(...Object.keys(m));
  });
  // DEV-24211: not a real Detections category (nothing in signatures.ts files
  // under it) - the "Pixels confirmed firing at runtime" check reads this via
  // cat(), so populate it from the wire-sourced tag fires a full scan's
  // runtime capture produces, or it can never report a hit.
  cats['Runtime-Confirmed Pixel Fires'] = Array.from(
    new Set(
      (result.tagFires ?? [])
        .filter(fire => fire.source === FireSource.WIRE)
        .map(fire => fire.platform)
    )
  ).sort();
  const text = names.join(' | ').toLowerCase();
  const hosts = [
    ...(result.runtimeNetwork?.thirdPartyHosts ?? []),
    ...(result.runtimeNetwork?.firstPartyHosts ?? [])
  ]
    .join(' ')
    .toLowerCase();
  const ins = result.advertiserInsights
    .map(i => i.title)
    .join(' | ')
    .toLowerCase();

  return {
    cat: c => cats[c] ?? [],
    has: pattern => pattern.test(text),
    host: pattern => pattern.test(hosts),
    insight: pattern => pattern.test(ins)
  };
};

export const ChannelScoreClass = {
  GOOD: 'good',
  WARN: 'warn',
  MISS: 'miss',
  LIMITED: 'lim'
} as const;
export type ChannelScoreClassType = (typeof ChannelScoreClass)[keyof typeof ChannelScoreClass];

export interface ChannelScore {
  label: string;
  cls: ChannelScoreClassType;
  pct: number | null;
}

/** Ported from app.py's chScore(). */
export const scoreChannel = (results: CheckResult[]): ChannelScore => {
  const scored = results.filter(([status]) => status !== 'info');
  if (scored.length === 0)
    return { label: 'Limited visibility', cls: ChannelScoreClass.LIMITED, pct: null };
  const statusPoints: Record<string, number> = { good: 1, warn: 0.5, miss: 0 };
  const points = scored.reduce((acc, [status]) => acc + statusPoints[status], 0);
  const pct = Math.round((100 * points) / scored.length);
  if (pct >= 75) return { label: 'Strong', cls: ChannelScoreClass.GOOD, pct };
  if (pct >= 40) return { label: 'Moderate', cls: ChannelScoreClass.WARN, pct };
  return { label: 'Needs attention', cls: ChannelScoreClass.MISS, pct };
};

export interface ChannelCheckOutcome {
  label: string;
  result: CheckResult;
}

export interface ChannelEvaluation {
  channel: Channel;
  results: ChannelCheckOutcome[];
  score: ChannelScore;
}

export interface ChannelHealthReport {
  visible: ChannelEvaluation[];
  hiddenNames: string[];
}

/** Ported from app.py's renderChannels() (minus DOM rendering / active-tab state). */
export const evaluateChannelHealth = (
  result: ScanResult,
  clientType: string
): ChannelHealthReport => {
  const ctx = buildChannelHealthContext(result);

  const visibleChannels = CHANNELS.filter(ch => !ch.types || ch.types.includes(clientType));
  const hiddenNames = CHANNELS.filter(ch => ch.types && !ch.types.includes(clientType)).map(
    ch => ch.name
  );

  const visible: ChannelEvaluation[] = visibleChannels.map(channel => {
    const results = channel.checks
      .filter(check => !check.types || check.types.includes(clientType))
      .map(check => ({ label: check.label, result: check.evaluate(ctx) }));
    return { channel, results, score: scoreChannel(results.map(r => r.result)) };
  });

  return { visible, hiddenNames };
};
